// src/index.ts
import { z } from "zod";
var userRoleSchema = z.enum([
  "super_admin",
  "internal_admin",
  "internal_staff",
  "client_admin",
  "client_agent"
]);
var projectStatusSchema = z.enum(["draft", "active", "paused", "archived"]);
var clientStatusSchema = z.enum(["active", "inactive", "prospect"]);
var sourceTypeSchema = z.enum(["manual_text", "manual_faq", "pdf", "docx"]);
var sourceStatusSchema = z.enum(["pending", "processing", "indexed", "failed"]);
var conversationStateSchema = z.enum([
  "ai_active",
  "waiting_for_human",
  "human_active",
  "closed"
]);
var messageSenderTypeSchema = z.enum(["visitor", "ai", "human", "system"]);
var deliveryStateSchema = z.enum(["queued", "sent", "failed"]);
var jobStatusSchema = z.enum(["created", "running", "success", "failed"]);
var jobTypeSchema = z.enum([
  "index_manual_source",
  "index_pdf_source",
  "index_docx_source",
  "aggregate_usage"
]);
var brandSettingsSchema = z.object({
  displayName: z.string().min(1),
  welcomeMessage: z.string().default("How can I help today?"),
  helperText: z.string().default(
    "\u039E\u03B5\u03BA\u03B9\u03BD\u03AE\u03C3\u03C4\u03B5 \u03BC\u03B5 \u03BC\u03B9\u03B1 \u03B5\u03C1\u03CE\u03C4\u03B7\u03C3\u03B7 \u03AE \u03B5\u03C0\u03B9\u03BB\u03AD\u03BE\u03C4\u03B5 \u03BC\u03AF\u03B1 \u03C0\u03C1\u03CC\u03C4\u03B1\u03C3\u03B7. \u039F \u03B2\u03BF\u03B7\u03B8\u03CC\u03C2 \u03B8\u03B1 \u03B1\u03C0\u03B1\u03BD\u03C4\u03AC \u03C3\u03C5\u03BD\u03C4\u03B7\u03C1\u03B7\u03C4\u03B9\u03BA\u03AC \u03CC\u03C4\u03B1\u03BD \u03B7 \u03B4\u03B9\u03B1\u03B8\u03AD\u03C3\u03B9\u03BC\u03B7 \u03B3\u03BD\u03CE\u03C3\u03B7 \u03B4\u03B5\u03BD \u03B5\u03AF\u03BD\u03B1\u03B9 \u03B1\u03C1\u03BA\u03B5\u03C4\u03AC \u03B9\u03C3\u03C7\u03C5\u03C1\u03AE."
  ),
  themeColor: z.string().default("#0C6A57"),
  accentColor: z.string().default("#F0B429"),
  textColor: z.string().default("#12261E"),
  logoUrl: z.string().url().optional().or(z.literal("")),
  suggestedPrompts: z.array(z.string()).default([]),
  widgetPlacement: z.enum(["bottom-right", "bottom-left"]).default("bottom-right")
});
var aiSettingsSchema = z.object({
  model: z.string().default("gpt-4o-mini"),
  systemPrompt: z.string().default(""),
  fallbackMessage: z.string().default("I do not have enough verified information to answer that confidently."),
  leadCaptureEnabled: z.boolean().default(true),
  humanHandoffEnabled: z.boolean().default(true),
  answerTemperature: z.number().min(0).max(2).default(0.2),
  maxContextChunks: z.number().int().min(1).max(12).default(6)
});
var retrievalSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  topK: z.number().int().min(1).max(20).default(6),
  scoreThreshold: z.number().min(0).max(1).default(0.35),
  chunkSize: z.number().int().min(200).max(2e3).default(900),
  chunkOverlap: z.number().int().min(0).max(400).default(150)
});
var complianceSettingsSchema = z.object({
  piiRedactionEnabled: z.boolean().default(false),
  dataRetentionDays: z.number().int().min(1).max(3650).default(365),
  allowTranscriptExport: z.boolean().default(true)
});
var projectSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  name: z.string().min(1),
  slug: z.string().min(1),
  status: projectStatusSchema,
  publicWidgetKey: z.string().min(16),
  allowedDomains: z.array(z.string()).default([]),
  primaryDomain: z.string().min(1),
  brandSettings: brandSettingsSchema,
  aiSettings: aiSettingsSchema,
  retrievalSettings: retrievalSettingsSchema,
  complianceSettings: complianceSettingsSchema,
  createdAt: z.any(),
  updatedAt: z.any()
});
var clientSchema = z.object({
  id: z.string(),
  companyName: z.string().min(1),
  status: clientStatusSchema,
  contactEmail: z.string().email(),
  contactPhone: z.string().optional().default(""),
  billingPlan: z.string().default("mvp"),
  notes: z.string().default(""),
  createdAt: z.any(),
  updatedAt: z.any()
});
var userSchema = z.object({
  uid: z.string(),
  email: z.string().email(),
  displayName: z.string().min(1),
  role: userRoleSchema,
  clientId: z.string().nullish().transform((value) => value ?? void 0),
  projectIds: z.array(z.string()).nullish().transform((value) => value ?? void 0),
  status: z.enum(["active", "disabled", "invited"]).default("active"),
  createdAt: z.any(),
  updatedAt: z.any()
});
var sourceMetadataSchema = z.record(z.string(), z.unknown()).default({});
var sourceSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  clientId: z.string(),
  type: sourceTypeSchema,
  title: z.string().min(1),
  originalUrl: z.string().optional(),
  storagePath: z.string().optional(),
  uploadFilename: z.string().optional(),
  status: sourceStatusSchema,
  indexingJobId: z.string().optional(),
  lastIndexedAt: z.any().optional(),
  chunkCount: z.number().int().nonnegative().default(0),
  errorMessage: z.string().optional(),
  metadata: sourceMetadataSchema,
  createdAt: z.any(),
  updatedAt: z.any()
});
var visitorInfoSchema = z.object({
  name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  company: z.string().optional(),
  locale: z.string().optional(),
  userAgent: z.string().optional(),
  currentUrl: z.string().optional()
});
var sourceReferenceSchema = z.object({
  sourceId: z.string(),
  title: z.string(),
  url: z.string().optional(),
  chunkIndex: z.number().int().nonnegative(),
  score: z.number().optional()
});
var tokenUsageSchema = z.object({
  promptTokens: z.number().int().nonnegative().default(0),
  completionTokens: z.number().int().nonnegative().default(0),
  embeddingTokens: z.number().int().nonnegative().default(0),
  totalTokens: z.number().int().nonnegative().default(0)
});
var messageSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  projectId: z.string(),
  role: z.enum(["user", "assistant", "system"]),
  senderType: messageSenderTypeSchema,
  content: z.string(),
  createdAt: z.any(),
  senderUserId: z.string().optional(),
  sourceReferences: z.array(sourceReferenceSchema).optional(),
  tokenUsage: tokenUsageSchema.optional(),
  costEstimate: z.number().optional(),
  deliveryState: deliveryStateSchema.optional()
});
var conversationSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  clientId: z.string(),
  sessionId: z.string(),
  visitorId: z.string(),
  channel: z.enum(["widget"]),
  state: conversationStateSchema,
  assignedTo: z.string().optional(),
  humanTakeover: z.boolean().default(false),
  leadCaptured: z.boolean().default(false),
  leadId: z.string().optional(),
  startedAt: z.any(),
  lastMessageAt: z.any(),
  visitorInfo: visitorInfoSchema.default({}),
  summary: z.string().optional(),
  tags: z.array(z.string()).optional(),
  lastMessagePreview: z.string().default(""),
  lastActorType: messageSenderTypeSchema.default("visitor"),
  unreadByClient: z.boolean().default(false),
  unreadByInternal: z.boolean().default(false),
  hiddenFromClientPortal: z.boolean().default(false),
  hiddenFromClientPortalAt: z.any().optional(),
  hiddenFromClientPortalBy: z.string().optional()
});
var leadSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  clientId: z.string(),
  conversationId: z.string(),
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
  company: z.string().optional(),
  note: z.string().optional(),
  source: z.string().default("widget"),
  createdAt: z.any(),
  transcriptSnapshot: z.array(messageSchema).optional(),
  status: z.enum(["new", "contacted", "qualified", "closed"]).default("new")
});
var eventSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  clientId: z.string(),
  type: z.string(),
  actorType: z.enum(["system", "visitor", "user"]),
  actorId: z.string().optional(),
  targetType: z.string(),
  targetId: z.string(),
  payload: z.record(z.string(), z.unknown()).default({}),
  createdAt: z.any()
});
var usageDailySchema = z.object({
  projectId: z.string(),
  date: z.string(),
  messageCount: z.number().int().nonnegative().default(0),
  promptTokens: z.number().int().nonnegative().default(0),
  completionTokens: z.number().int().nonnegative().default(0),
  embeddingTokens: z.number().int().nonnegative().default(0),
  estimatedCost: z.number().nonnegative().default(0),
  conversationCount: z.number().int().nonnegative().default(0),
  uniqueVisitorCount: z.number().int().nonnegative().default(0)
});
var jobSchema = z.object({
  id: z.string(),
  type: jobTypeSchema,
  projectId: z.string(),
  sourceId: z.string().optional(),
  status: jobStatusSchema,
  progress: z.number().min(0).max(100).default(0),
  error: z.string().optional(),
  startedAt: z.any().optional(),
  finishedAt: z.any().optional(),
  createdBy: z.string(),
  createdAt: z.any()
});
var createClientInputSchema = clientSchema.pick({
  companyName: true,
  status: true,
  contactEmail: true,
  contactPhone: true,
  billingPlan: true,
  notes: true
});
var createProjectInputSchema = z.object({
  clientId: z.string(),
  name: z.string().min(1),
  slug: z.string().min(1),
  primaryDomain: z.string().min(1),
  allowedDomains: z.array(z.string()).default([]),
  brandSettings: brandSettingsSchema,
  aiSettings: aiSettingsSchema,
  retrievalSettings: retrievalSettingsSchema,
  complianceSettings: complianceSettingsSchema,
  status: projectStatusSchema.default("active")
});
var updateProjectInputSchema = createProjectInputSchema.partial().extend({
  projectId: z.string()
});
var createPlatformUserInputSchema = z.object({
  email: z.string().email(),
  displayName: z.string().min(1),
  role: userRoleSchema,
  clientId: z.string().nullish().transform((value) => value ?? void 0),
  projectIds: z.array(z.string()).nullish().transform((value) => value ?? void 0),
  password: z.string().min(8).optional()
});
var addSourceInputSchema = z.object({
  projectId: z.string(),
  type: sourceTypeSchema,
  title: z.string().min(1),
  originalUrl: z.string().nullish().transform((value) => value ?? void 0),
  content: z.string().nullish().transform((value) => value ?? void 0),
  faqEntries: z.array(
    z.object({
      question: z.string(),
      answer: z.string()
    })
  ).nullish().transform((value) => value ?? void 0),
  filePath: z.string().nullish().transform((value) => value ?? void 0),
  uploadFilename: z.string().nullish().transform((value) => value ?? void 0),
  metadata: sourceMetadataSchema.nullish().transform((value) => value ?? void 0)
});
var triggerReindexInputSchema = z.object({
  projectId: z.string(),
  sourceId: z.string()
});
var deleteSourceInputSchema = z.object({
  projectId: z.string(),
  sourceId: z.string()
});
var listConversationsInputSchema = z.object({
  projectId: z.string().optional(),
  clientId: z.string().optional(),
  state: conversationStateSchema.optional(),
  limit: z.number().int().min(1).max(100).default(50)
});
var getConversationInputSchema = z.object({
  projectId: z.string(),
  conversationId: z.string()
});
var takeoverConversationInputSchema = getConversationInputSchema.extend({
  assignToUserId: z.string().optional()
});
var resumeAiConversationInputSchema = getConversationInputSchema;
var sendHumanMessageInputSchema = getConversationInputSchema.extend({
  content: z.string().min(1)
});
var hideConversationInputSchema = getConversationInputSchema;
var listLeadsInputSchema = z.object({
  projectId: z.string().optional(),
  clientId: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50)
});
var getUsageSummaryInputSchema = z.object({
  projectId: z.string().optional(),
  clientId: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional()
});
var listJobsInputSchema = z.object({
  projectId: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50)
});
var listProjectsInputSchema = z.object({
  clientId: z.string().optional()
});
var listClientsInputSchema = z.object({
  status: clientStatusSchema.optional()
});
var listUsersInputSchema = z.object({
  clientId: z.string().optional(),
  role: userRoleSchema.optional()
});
var listSourcesInputSchema = z.object({
  projectId: z.string().optional()
});
var fetchPublicConfigInputSchema = z.object({
  widgetKey: z.string().min(16),
  origin: z.string().optional()
});
var publicWidgetConfigSchema = z.object({
  projectId: z.string(),
  widgetKey: z.string(),
  displayName: z.string(),
  welcomeMessage: z.string(),
  helperText: z.string(),
  themeColor: z.string(),
  accentColor: z.string(),
  textColor: z.string(),
  suggestedPrompts: z.array(z.string()),
  handoffEnabled: z.boolean(),
  leadCaptureEnabled: z.boolean(),
  widgetPlacement: z.enum(["bottom-right", "bottom-left"])
});
var initChatInputSchema = z.object({
  widgetKey: z.string().min(16),
  origin: z.string().optional(),
  sessionId: z.string().optional(),
  visitorId: z.string().optional(),
  currentUrl: z.string().optional()
});
var initChatResponseSchema = z.object({
  conversationId: z.string(),
  conversationToken: z.string(),
  projectId: z.string(),
  sessionId: z.string(),
  visitorId: z.string(),
  state: conversationStateSchema,
  messages: z.array(messageSchema)
});
var sendMessageInputSchema = z.object({
  widgetKey: z.string().min(16),
  projectId: z.string(),
  conversationId: z.string(),
  conversationToken: z.string().min(10),
  content: z.string().min(1),
  origin: z.string().optional(),
  currentUrl: z.string().optional()
});
var sendMessageResponseSchema = z.object({
  state: conversationStateSchema,
  reply: z.string(),
  message: messageSchema.optional(),
  sourceReferences: z.array(sourceReferenceSchema).optional(),
  leadCaptureSuggested: z.boolean().default(false),
  handoffSuggested: z.boolean().default(false)
});
var submitLeadInputSchema = z.object({
  widgetKey: z.string(),
  projectId: z.string(),
  conversationId: z.string(),
  conversationToken: z.string(),
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string().optional(),
  company: z.string().optional(),
  note: z.string().optional()
});
var pollConversationUpdatesInputSchema = z.object({
  widgetKey: z.string(),
  projectId: z.string(),
  conversationId: z.string(),
  conversationToken: z.string(),
  after: z.string().optional()
});
var conversationListItemSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  clientId: z.string(),
  lastMessagePreview: z.string(),
  lastActorType: messageSenderTypeSchema,
  state: conversationStateSchema,
  lastMessageAt: z.any(),
  unreadByClient: z.boolean(),
  unreadByInternal: z.boolean(),
  visitorInfo: visitorInfoSchema
});
export {
  addSourceInputSchema,
  aiSettingsSchema,
  brandSettingsSchema,
  clientSchema,
  clientStatusSchema,
  complianceSettingsSchema,
  conversationListItemSchema,
  conversationSchema,
  conversationStateSchema,
  createClientInputSchema,
  createPlatformUserInputSchema,
  createProjectInputSchema,
  deleteSourceInputSchema,
  deliveryStateSchema,
  eventSchema,
  fetchPublicConfigInputSchema,
  getConversationInputSchema,
  getUsageSummaryInputSchema,
  hideConversationInputSchema,
  initChatInputSchema,
  initChatResponseSchema,
  jobSchema,
  jobStatusSchema,
  jobTypeSchema,
  leadSchema,
  listClientsInputSchema,
  listConversationsInputSchema,
  listJobsInputSchema,
  listLeadsInputSchema,
  listProjectsInputSchema,
  listSourcesInputSchema,
  listUsersInputSchema,
  messageSchema,
  messageSenderTypeSchema,
  pollConversationUpdatesInputSchema,
  projectSchema,
  projectStatusSchema,
  publicWidgetConfigSchema,
  resumeAiConversationInputSchema,
  retrievalSettingsSchema,
  sendHumanMessageInputSchema,
  sendMessageInputSchema,
  sendMessageResponseSchema,
  sourceMetadataSchema,
  sourceReferenceSchema,
  sourceSchema,
  sourceStatusSchema,
  sourceTypeSchema,
  submitLeadInputSchema,
  takeoverConversationInputSchema,
  tokenUsageSchema,
  triggerReindexInputSchema,
  updateProjectInputSchema,
  usageDailySchema,
  userRoleSchema,
  userSchema,
  visitorInfoSchema
};
